import { useState, useCallback } from 'react';
import { checkBundleStatus, analyzeOrderForBundleAndExtras } from '../utils/bundleCalculations';
import { calculateTotal } from '../utils/orderCalculations';

/**
 * Custom hook to manage purchase state and bundle logic
 * Centralizes all bundle-related state management and business logic
 */
export const usePurchaseState = () => {
    // Bundle state (tightly coupled)
    const [bundleState, setBundleState] = useState({
        options: [],
        selected: null,
        items: [],
        status: 'none', // 'none', 'complete', 'incomplete'
        isCustomPurchase: false,
        disableBundleDetection: false, // Prevent auto-detection after manual selection
        actualAllocations: new Map() // Track actual quantities allocated to bundle per itemOptionId
    });
    
    // Track additional (extra) quantities separately from bundle
    const [additionalQuantities, setAdditionalQuantities] = useState(new Map());

    const updateBundleStatus = useCallback((order) => {
        const status = checkBundleStatus(bundleState.selected, order);
        setBundleState(prev => ({ ...prev, status }));
    }, [bundleState.selected]);

    // Helper functions to consolidate state updates and reduce race conditions
    const updateBundleState = useCallback((updates) => {
        setBundleState(prev => ({ ...prev, ...updates }));
    }, []);

    const selectBundle = useCallback((bundle, items = [], status = 'complete') => {
        updateBundleState({
            selected: bundle,
            items,
            status,
            isCustomPurchase: false,
            disableBundleDetection: true // Prevent auto-detection after manual selection
        });
    }, [updateBundleState]);

    const resetToCustomPurchase = useCallback(() => {
        updateBundleState({
            selected: null,
            items: [],
            status: 'none',
            isCustomPurchase: true,
            disableBundleDetection: true // Disable auto-detection for custom purchase
        });
    }, [updateBundleState]);

    const removeBundle = useCallback(() => {
        updateBundleState({
            selected: null,
            items: [],
            status: 'none'
            // Keep isCustomPurchase and disableBundleDetection as they were
        });
    }, [updateBundleState]);

    const loadBundleOptions = useCallback((options) => {
        setBundleState(prev => {
            // Only set isCustomPurchase if user hasn't manually selected custom purchase
            const shouldSetCustomPurchase = (!options || options.length === 0) && !prev.disableBundleDetection;
            
            return {
                ...prev, 
                options: options || [],
                // Only override isCustomPurchase if user hasn't made a manual choice
                ...(shouldSetCustomPurchase && { isCustomPurchase: true })
            };
        });
    }, []);

    const handleBundleSelection = useCallback((bundle, preserveQuantities = false, order, setOrder) => {
        if (!bundle) {
            // Custom Purchase selected
            resetToCustomPurchase();
            
            // Reset additional quantities
            setAdditionalQuantities(new Map());
            
            // Only reset quantities if not preserving them (for order edits)
            if (!preserveQuantities && order && order.fundraiserOrderItems) {
                setOrder(prevOrder => {
                    const updatedOrder = {
                        ...prevOrder,
                        fundraiserOrderItems: prevOrder.fundraiserOrderItems.map(item => ({
                            ...item,
                            fundraiserOrderItemOptions: item.fundraiserOrderItemOptions.map(option => ({
                                ...option,
                                qty: 0
                            }))
                        }))
                    };
                    const newTotal = calculateTotal(updatedOrder);
                    return { ...updatedOrder, orderTotal: newTotal };
                });
            }
            return;
        }

        // Bundle selected - disable auto-detection going forward for manual selections
        updateBundleState({
            selected: bundle,
            isCustomPurchase: false,
            disableBundleDetection: true // Prevent auto-detection after manual selection
        });
        
        // Reset additional quantities when new bundle selected (unless preserving for edits)
        if (!preserveQuantities) {
            setAdditionalQuantities(new Map());
        }
        
        // Apply bundle quantities to the options (immutably)
        if (order && order.fundraiserOrderItems) {
            // Build quantities map from bundle
            const bundleQuantities = new Map();
            bundle.optionBundleEntries.forEach(bundleEntry => {
                bundleQuantities.set(bundleEntry.itemsOptionId, bundleEntry.quantity);
            });
            
            setOrder(prevOrder => {
                const updatedOrder = {
                    ...prevOrder,
                    fundraiserOrderItems: prevOrder.fundraiserOrderItems.map(item => ({
                        ...item,
                        fundraiserOrderItemOptions: item.fundraiserOrderItemOptions.map(option => {
                            const bundleQty = bundleQuantities.get(option.itemOptionId) || 0;
                            const newQty = preserveQuantities ? Math.max(option.qty, bundleQty) : bundleQty;
                            return { ...option, qty: newQty };
                        })
                    }))
                };
                const newTotal = calculateTotal(updatedOrder);
                return { ...updatedOrder, orderTotal: newTotal };
            });
        }
    }, [resetToCustomPurchase, updateBundleState]);

    const handleOptionChange = useCallback((bundleEntry, newOptionId, order, setOrder) => {
        if (!order || !order.fundraiserOrderItems) return;
        
        // Calculate current bundle quantities before change
        const oldBundleQuantities = new Map();
        bundleState.selected.optionBundleEntries.forEach(entry => {
            const currentQty = oldBundleQuantities.get(entry.itemsOptionId) || 0;
            oldBundleQuantities.set(entry.itemsOptionId, currentQty + entry.quantity);
        });
        
        // Update the selectedBundle state to persist the change (immutably)
        const updatedBundle = {
            ...bundleState.selected,
            optionBundleEntries: bundleState.selected.optionBundleEntries.map(entry =>
                entry === bundleEntry
                    ? { ...entry, itemsOptionId: parseInt(newOptionId) }
                    : entry
            )
        };
        updateBundleState({ selected: updatedBundle });
        
        // Calculate new bundle quantities after change
        const newBundleQuantities = new Map();
        updatedBundle.optionBundleEntries.forEach(entry => {
            const currentQty = newBundleQuantities.get(entry.itemsOptionId) || 0;
            newBundleQuantities.set(entry.itemsOptionId, currentQty + entry.quantity);
        });
        
        // Update order quantities preserving extras
        setOrder(prevOrder => {
            const updatedOrder = {
                ...prevOrder,
                fundraiserOrderItems: prevOrder.fundraiserOrderItems.map(item => ({
                    ...item,
                    fundraiserOrderItemOptions: item.fundraiserOrderItemOptions.map(option => {
                        const oldBundleQty = oldBundleQuantities.get(option.itemOptionId) || 0;
                        const newBundleQty = newBundleQuantities.get(option.itemOptionId) || 0;
                        const currentQty = option.qty;
                        
                        // Calculate extras (quantity above bundle requirement)
                        const extraQty = Math.max(0, currentQty - oldBundleQty);
                        
                        // Set new quantity as new bundle requirement + preserved extras
                        const newQty = newBundleQty + extraQty;
                        
                        return { ...option, qty: newQty };
                    })
                }))
            };
            const newTotal = calculateTotal(updatedOrder);
            return { ...updatedOrder, orderTotal: newTotal };
        });
        
        // Build bundle items for display and update actual allocations
        const bundleItemsData = [];
        const actualAllocations = new Map();
        
        updatedBundle.optionBundleEntries.forEach(bundleEntry => {
            // Set actual allocation for this bundle entry
            const currentAllocation = actualAllocations.get(bundleEntry.itemsOptionId) || 0;
            actualAllocations.set(bundleEntry.itemsOptionId, currentAllocation + bundleEntry.quantity);
            
            order.fundraiserOrderItems.forEach(item => {
                const matchingOption = item.fundraiserOrderItemOptions.find(
                    option => option.itemOptionId === bundleEntry.itemsOptionId
                );
                if (matchingOption) {
                    bundleItemsData.push({
                        name: matchingOption.name,
                        price: matchingOption.price,
                        quantity: bundleEntry.quantity
                    });
                }
            });
        });
        
        updateBundleState({ 
            items: bundleItemsData,
            actualAllocations: actualAllocations
        });
        updateBundleStatus(order);
    }, [bundleState.selected, updateBundleState, updateBundleStatus]);

    const getBundleQuantity = useCallback((itemOptionId) => {
        if (!bundleState.selected || !bundleState.actualAllocations) return 0;
        
        // Return the actual allocated quantity for this itemOptionId
        return bundleState.actualAllocations.get(itemOptionId) || 0;
    }, [bundleState.selected, bundleState.actualAllocations]);

    const handleRemoveBundle = useCallback((order, setOrder) => {
        removeBundle();
        
        // Reset all quantities to 0 (immutably)
        if (order && order.fundraiserOrderItems) {
            setOrder(prevOrder => {
                const updatedOrder = {
                    ...prevOrder,
                    fundraiserOrderItems: prevOrder.fundraiserOrderItems.map(item => ({
                        ...item,
                        fundraiserOrderItemOptions: item.fundraiserOrderItemOptions.map(option => ({
                            ...option,
                            qty: 0
                        }))
                    }))
                };
                const newTotal = calculateTotal(updatedOrder);
                return { ...updatedOrder, orderTotal: newTotal };
            });
        }
    }, [removeBundle]);

    const analyzeBundleForOrder = useCallback((order) => {
        if (!order || !bundleState.options.length) return null;
        return analyzeOrderForBundleAndExtras(bundleState.options, order);
    }, [bundleState.options]);

    return {
        // State
        bundleState,
        additionalQuantities,
        
        // Actions
        selectBundle,
        resetToCustomPurchase,
        removeBundle,
        loadBundleOptions,
        handleBundleSelection,
        handleOptionChange,
        handleRemoveBundle,
        updateBundleStatus,
        getBundleQuantity,
        analyzeBundleForOrder,
        
        // State setters for external use
        setAdditionalQuantities,
        updateBundleState,
        
        // Computed values
        isCustomPurchase: bundleState.isCustomPurchase,
        selectedBundle: bundleState.selected,
        bundleOptions: bundleState.options,
        bundleItems: bundleState.items,
        bundleStatus: bundleState.status
    };
};
